/**
 * Drawing a body between the places it was actually seen.
 *
 * Snapshots arrive fifteen times a second and the screen draws sixty, so a
 * position applied the moment it lands is held for four frames and then
 * jumps. Everybody else in the world moves like a chess piece.
 *
 * **Drawn a snapshot behind, never ahead.** The alternative is to guess where
 * they are going and correct when the next one disagrees, which looks worse
 * in exactly the case it is meant to help: somebody who stops dead is walked
 * on past the wall and yanked back. A sixty-sixth of a second of lag nobody
 * can feel buys an interpolation that is always between two places the body
 * really was.
 *
 * Pure, and in core, because the same arithmetic has to serve the people and
 * the orcs - and because "did it lerp the short way round the circle" is a
 * question with an answer that should not need a socket to ask.
 */

/** Where a body is, and which way it is facing. */
export interface Where {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
}

/**
 * The shortest way round from one angle to another.
 *
 * Yaw wraps, so a body turning from just under π to just over it is a tenth
 * of a radian away and not six and a bit. Lerping the long way spins them on
 * the spot - which is what happened, once, to everybody walking north.
 */
export function turn(from: number, to: number, t: number): number {
  const TAU = Math.PI * 2;
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return from + d * t;
}

function between(a: Where, b: Where, t: number): Where {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    yaw: turn(a.yaw, b.yaw, t),
    pitch: a.pitch + (b.pitch - a.pitch) * t,
  };
}

/**
 * How far behind the newest snapshot to draw.
 *
 * One snapshot's worth. Less and a late arrival leaves nothing to
 * interpolate towards and the body stalls; much more and you are watching
 * somebody's past for no reason.
 */
export const TRAIL_DELAY = 1 / 15;

/** One body's last two snapshots, and where it is between them. */
export class Trail {
  /**
   * How far behind the newest point this is drawn. One snapshot's worth
   * by default; a stream that comes less often is given a longer one,
   * or the body jumps to the middle of each new leg and then waits.
   */
  constructor(private readonly delay = TRAIL_DELAY) {}

  private older: Where | null = null;
  private olderAt = 0;
  private newer: Where | null = null;
  private newerAt = 0;

  /**
   * A snapshot arrived.
   *
   * A repeat of the one we have is not a new place, only a new time - taking
   * it as one would collapse the gap between them to nothing and put the
   * interpolation back where it started, which draws the stutter this whole
   * file exists to remove. Standing still is a body whose two snapshots agree.
   */
  push(where: Where, at: number): void {
    if (this.newer === null) {
      this.older = where; this.olderAt = at;
      this.newer = where; this.newerAt = at;
      return;
    }
    if (at <= this.newerAt) return;
    this.older = this.newer; this.olderAt = this.newerAt;
    this.newer = where; this.newerAt = at;
  }

  /** Nothing has ever been pushed, so there is nowhere to draw it. */
  get empty(): boolean { return this.newer === null; }

  /**
   * Where to draw it at this moment.
   *
   * Clamped at both ends: before the older snapshot it sits still rather than
   * running backwards, and past the newer one it waits there rather than
   * carrying on into a wall.
   */
  at(now: number): Where {
    const newer = this.newer;
    if (newer === null) throw new Error("a trail with nothing in it");
    const older = this.older ?? newer;
    const span = this.newerAt - this.olderAt;
    if (span <= 0) return newer;
    const t = (now - this.delay - this.olderAt) / span;
    return between(older, newer, Math.min(1, Math.max(0, t)));
  }

  /**
   * How fast it is travelling, flat, in blocks a second.
   *
   * From the two snapshots rather than from frame to frame: the legs are
   * driven by this, and a per-frame difference of an interpolated position is
   * the same number divided by four, which had everybody tiptoeing.
   */
  get speed(): number {
    const newer = this.newer;
    const older = this.older;
    if (newer === null || older === null) return 0;
    const span = this.newerAt - this.olderAt;
    if (span <= 0) return 0;
    return Math.hypot(newer.x - older.x, newer.z - older.z) / span;
  }
}
